import {
  ApolloClient,
  ApolloLink,
  from,
  InMemoryCache,
  type NormalizedCacheObject,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import { persistCache } from 'apollo3-cache-persist';
import { SelectiveCachePersistence } from './cache/selective-cache-persistence';
import {
  CACHE_PERSISTENCE_CONFIG,
  DEV_CACHE_PERSISTENCE_CONFIG,
} from './cache/cache-config';
import { ALLOWED_OPERATIONS } from './constants';

export async function getApolloClient(
  token: string,
): Promise<ApolloClient<NormalizedCacheObject>> {
  const authMiddleware = new ApolloLink((operation, forward) => {
    if (token) {
      operation.setContext({
        headers: {
          authorization: `Bearer ${token}`,
        },
      });
    } else if (
      !operation.operationName ||
      !ALLOWED_OPERATIONS.includes(
        operation.operationName as (typeof ALLOWED_OPERATIONS)[number],
      )
    ) {
      throw new Error('No access token available.');
    }

    return forward(operation);
  });

  // Create selective cache persistence instance with environment-based configuration
  const isProduction = import.meta.env.PROD;
  const cacheConfig = isProduction
    ? CACHE_PERSISTENCE_CONFIG
    : DEV_CACHE_PERSISTENCE_CONFIG;
  const cachePersistence = new SelectiveCachePersistence(cacheConfig);

  const cache = new InMemoryCache({
    typePolicies: {
      ProcessorCapabilitySpecification: {
        fields: {
          specifications: {
            // Don't merge specifications arrays - keep them separate per parent
            merge: false,
          },
        },
      },
      ProcessorCapabilitySpecificationSpecifications: {
        // Disable normalization to prevent cache key collisions
        keyFields: false,
        fields: {
          // Don't merge options arrays - keep them separate per parent specification
          options: {
            merge: false,
          },
        },
      },
      ProcessorCapabilitySpecificationSpecificationsOptions: {
        // Disable normalization to prevent cache key collisions for options too
        keyFields: false,
        fields: {
          options: {
            merge: false,
          },
        },
      },
    },
  });

  // Create a custom storage wrapper that filters data
  const selectiveStorage = {
    getItem: async (key: string): Promise<string | null> => {
      const data = window.localStorage.getItem(key);
      if (!data) return null;

      try {
        const persistentData = JSON.parse(data);
        const restoredCache =
          cachePersistence.restoreCacheFromPersistence(persistentData);

        return JSON.stringify(restoredCache);
      } catch (_error) {
        return data; // Fallback to original data
      }
    },

    setItem: async (key: string, value: string): Promise<void> => {
      try {
        const cacheData = JSON.parse(value);
        const persistentData =
          cachePersistence.filterCacheForPersistence(cacheData);
        const prunedData = cachePersistence.pruneBySize(persistentData);

        window.localStorage.setItem(key, JSON.stringify(prunedData));
      } catch (_error) {
        window.localStorage.setItem(key, value); // Fallback to original data
      }
    },

    removeItem: async (key: string): Promise<void> => {
      window.localStorage.removeItem(key);
    },
  };

  // Set up cache persistence with custom storage
  await persistCache({
    cache,
    storage: selectiveStorage,
    key: `apollo-cache-persist-${token || 'guest'}`,
  });

  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path }) =>
        // eslint-disable-next-line no-console
        console.error(
          `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
        ),
      );
    }

    if (networkError) {
      // eslint-disable-next-line no-console
      console.error(`[Network error]: ${networkError}`);
    }
  });

  // Debug link to log all network requests and cache operations
  const debugLink = new ApolloLink((operation, forward) => {
    if (!import.meta.env.VITE_ENABLE_DETAILED_APOLLO_LOGGING) {
      return forward(operation);
    }
    // eslint-disable-next-line no-console
    console.log('[Apollo Network] Starting request:', {
      operationName: operation.operationName,
      variables: operation.variables,
      timestamp: new Date().toISOString(),
    });

    return forward(operation).map((response) => {
      console.log('[Apollo Network] Received response:', {
        operationName: operation.operationName,
        response,
        timestamp: new Date().toISOString(),
      });
      return response;
    });
  });

  const httpLink = createUploadLink({
    uri: `${import.meta.env?.VITE_GRAPHQL_URL || '/'}api/graphql`,
    headers: {
      'keep-alive': 'true',
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      Pragma: 'no-cache',
    },
  });

  return new ApolloClient({
    link: from([
      errorLink,
      debugLink,
      authMiddleware,
      httpLink as unknown as ApolloLink,
    ]),
    cache,
    credentials: 'same-origin',
    connectToDevTools: !import.meta.env.PROD,
  });
}
